// src/shared/navigation/navTree.ts
// ────────────────────────────────────────────────────────────────────────────
// Declarative navigation tree consumed by BubbleNav (mobile).
//
// Each node maps to a route-path + search-params combination so the bubble
// can deep-link into ANY tab/sub-tab by composing the right URL.
//
// URL param keys used:
//   devTab   → DeveloperDashboard   (urlKey in Tabs)
//   simTab   → SimulatorPanel       (urlKey in Tabs)
//   gApiTab  → GoogleApisSection    (read on mount)
//   adminTab → AdminDashboard       (urlKey in Tabs)
//   iamTab   → IAMManagement        (read on mount)
//   toView   → TableOpsAdmin        (read on mount)
//   topView  → TableOpsPanel        (read on mount)
//   orchTab  → OrchestratorManagement (urlKey in Tabs)
//   scopeTab → ScopeCenterPanel       (read on mount)
//   runnerTab → WorkflowRunnerPanel   (urlKey in Tabs)
// ────────────────────────────────────────────────────────────────────────────

export interface NavNode {
  /** Unique identifier within the tree */
  id: string;
  /** Display label */
  label: string;
  /** Route path — only top-level pages carry this; children inherit. */
  path?: string;
  /** URL search-params to apply on navigation (leaf params are self-contained). */
  params?: Record<string, string>;
  /** Nested children (expandable in the bubble overlay). */
  children?: NavNode[];
  /**
   * Permission gate — user must hold **at least one** of these abilities
   * (or wildcard `*`) for this node to be visible.  If omitted the node
   * inherits the parent's visibility.
   */
  need?: string[];
}

// ── Full tree ────────────────────────────────────────────────────────────────

export const NAV_TREE: NavNode[] = [
  {
    id: 'sales-purchase',
    label: 'Sales / Purchase',
    path: '/sales-purchase',
    need: ['canManageServices'],
    children: [
      { id: 'sp-provisioning', label: 'Provisioning', params: { spTab: 'Provisioning' } },
      { id: 'sp-metrics',      label: 'Metrics',      params: { spTab: 'Metrics' } },
    ],
  },
  {
    id: 'developer',
    label: 'Developer',
    path: '/developer',
    need: ['canViewDeveloper'],
    children: [
      {
        id: 'dev-sim',
        label: 'Simulator',
        params: { devTab: 'Simulator' },
        children: [
          { id: 'sim-electron',    label: 'Electron',    params: { devTab: 'Simulator', simTab: 'Electron' } },
          { id: 'sim-string',      label: 'String',      params: { devTab: 'Simulator', simTab: 'String' } },
          { id: 'sim-music',       label: 'Music',       params: { devTab: 'Simulator', simTab: 'Music' } },
          { id: 'sim-cadabra',     label: 'KDABRA',      params: { devTab: 'Simulator', simTab: 'KDABRA' } },
          { id: 'sim-midi-daw',   label: 'MIDI DAW',    params: { devTab: 'Simulator', simTab: 'MidiDAW' } },
          {
            id: 'sim-tableops',
            label: 'TableOps',
            params: { devTab: 'Simulator', simTab: 'TableOps' },
            children: [
              { id: 'top-floorplan', label: 'Floor Plan', params: { devTab: 'Simulator', simTab: 'TableOps', topView: 'floorplan' } },
              { id: 'top-order',     label: 'Orders',     params: { devTab: 'Simulator', simTab: 'TableOps', topView: 'order' } },
              { id: 'top-kds',       label: 'KDS',        params: { devTab: 'Simulator', simTab: 'TableOps', topView: 'kds' } },
              { id: 'top-checkout',   label: 'Checkout',   params: { devTab: 'Simulator', simTab: 'TableOps', topView: 'checkout' } },
              { id: 'top-dashboard',  label: 'Dashboard',  params: { devTab: 'Simulator', simTab: 'TableOps', topView: 'dashboard' } },
              { id: 'top-settings',   label: 'Settings',   params: { devTab: 'Simulator', simTab: 'TableOps', topView: 'settings' } },
            ],
          },
          { id: 'sim-mercadopago', label: 'MercadoPago', params: { devTab: 'Simulator', simTab: 'MercadoPago' } },
          { id: 'sim-steprender', label: 'Step Render', params: { devTab: 'Simulator', simTab: 'StepRender' } },
          { id: 'sim-traffic',     label: 'Traffic Sim', params: { devTab: 'Simulator', simTab: 'TrafficSim' }, need: ['isSuperadmin'] },
          { id: 'sim-daemonops',   label: 'DaemonOps',   params: { devTab: 'Simulator', simTab: 'DaemonOps' }, need: ['isSuperadmin'] },
        ],
      },
      { id: 'dev-logger',   label: 'Logger',   params: { devTab: 'Logger' } },
      { id: 'dev-testing',  label: 'Testing',  params: { devTab: 'Testing' } },
      { id: 'dev-videos',   label: 'Videos',   params: { devTab: 'Videos' } },
      {
        id: 'dev-workflow',
        label: 'Workflow',
        params: { devTab: 'Workflow' },
        need: ['canViewWorkflows'],
        children: [
          { id: 'wf-runner',   label: 'Runner',          params: { devTab: 'Workflow', runnerTab: 'Workflows/Runner' } },
          { id: 'wf-activity', label: 'Activity Log',    params: { devTab: 'Workflow', runnerTab: 'Activity Log' } },
          { id: 'wf-stats',    label: 'Statistics',      params: { devTab: 'Workflow', runnerTab: 'Statistics' } },
          { id: 'wf-outcomes', label: 'Outcomes',        params: { devTab: 'Workflow', runnerTab: 'Outcomes' } },
          { id: 'wf-scopes',   label: 'Scopes',          params: { devTab: 'Workflow', runnerTab: 'Scopes' } },
          { id: 'wf-ai',       label: 'AI Consume',      params: { devTab: 'Workflow', runnerTab: 'AI CONSUME' } },
          { id: 'wf-media',    label: 'Audio Split',     params: { devTab: 'Workflow', runnerTab: 'media.audio.split' } },
        ],
      },
      {
        id: 'dev-telemetry',
        label: 'Telemetry',
        params: { devTab: 'Telemetry' },
        // Same gate as the top-level /telemetry-center entry.
        need: ['canViewIotDevices'],
      },
      {
        id: 'dev-google',
        label: 'Google APIs',
        params: { devTab: 'Google APIs' },
        children: [
          { id: 'gapi-tts',     label: 'Text-to-Speech',  params: { devTab: 'Google APIs', gApiTab: 'Text-to-Speech' } },
          { id: 'gapi-stt',     label: 'Speech-to-Text',  params: { devTab: 'Google APIs', gApiTab: 'Speech-to-Text' } },
          { id: 'gapi-icongen', label: 'Icon Generator',   params: { devTab: 'Google APIs', gApiTab: 'Icon Generator' } },
        ],
      },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    path: '/admin',
    need: ['canViewAdmin'],
    children: [
      {
        id: 'admin-iam',
        label: 'IAM',
        params: { adminTab: 'IAM' },
        need: ['canManageUsers', 'canManageRbac', 'canManageTenants'],
        children: [
          { id: 'iam-tenants',   label: 'Tenants',         params: { adminTab: 'IAM', iamTab: 'tenants' },   need: ['canManageTenants'] },
          { id: 'iam-users',     label: 'Users',           params: { adminTab: 'IAM', iamTab: 'users' },     need: ['canManageUsers'] },
          { id: 'iam-rbac',      label: 'RBAC',            params: { adminTab: 'IAM', iamTab: 'rbac' },      need: ['canManageRbac'] },
          { id: 'iam-identity',  label: 'Identity Linking',params: { adminTab: 'IAM', iamTab: 'identity' },  need: ['canManageUsers'] },
          { id: 'iam-templates', label: 'Email Templates', params: { adminTab: 'IAM', iamTab: 'templates' }, need: ['canManageUsers'] },
        ],
      },
      {
        id: 'admin-orch',
        label: 'Orchestrator',
        params: { adminTab: 'Orchestrator Management' },
        need: ['canManageServices', 'canManageOrchestrator'],
        children: [
          { id: 'orch-workers',   label: 'Workers',          params: { adminTab: 'Orchestrator Management', orchTab: 'Workers' } },
          { id: 'orch-handlers',  label: 'Handler Catalog',  params: { adminTab: 'Orchestrator Management', orchTab: 'Handler Catalog' } },
          { id: 'orch-incomes',   label: 'Incomes v2',       params: { adminTab: 'Orchestrator Management', orchTab: 'Incomes v2' } },
          { id: 'orch-outcomes',  label: 'Outcomes',         params: { adminTab: 'Orchestrator Management', orchTab: 'Outcomes' } },
          { id: 'orch-workflow',  label: 'Workflow',         params: { adminTab: 'Orchestrator Management', orchTab: 'Workflow' } },
          { id: 'orch-cleanup',   label: 'Cleanup',          params: { adminTab: 'Orchestrator Management', orchTab: 'Cleanup' } },
          {
            id: 'orch-scope-center',
            label: 'Scope Center',
            params: { adminTab: 'Orchestrator Management', orchTab: 'Scope Center' },
            children: [
              { id: 'scope-migrate',  label: 'Migration',      params: { adminTab: 'Orchestrator Management', orchTab: 'Scope Center', scopeTab: 'Migration' } },
              { id: 'scope-cleaner',  label: 'Cleaner',        params: { adminTab: 'Orchestrator Management', orchTab: 'Scope Center', scopeTab: 'Cleaner' } },
              { id: 'scope-kpi',      label: 'KPI Analytics',  params: { adminTab: 'Orchestrator Management', orchTab: 'Scope Center', scopeTab: 'KPI Analytics' } },
            ],
          },
          { id: 'orch-edge-tasks', label: 'Edge Tasks',      params: { adminTab: 'Orchestrator Management', orchTab: 'Edge Tasks' }, need: ['canViewWorkflows'] },
        ],
      },
      {
        id: 'admin-tableops',
        label: 'Table Ops',
        params: { adminTab: 'Table Ops' },
        need: ['canAdminTableOps', 'canViewProducts'],
        children: [
          { id: 'to-editor',   label: 'Map Editor',  params: { adminTab: 'Table Ops', toView: 'editor' } },
          { id: 'to-entities', label: 'Entities',    params: { adminTab: 'Table Ops', toView: 'entities' } },
          { id: 'to-manager',  label: 'Menu',        params: { adminTab: 'Table Ops', toView: 'manager' }, need: ['canAdminTableOps'] },
          { id: 'to-menu',     label: 'Menu Import', params: { adminTab: 'Table Ops', toView: 'menu' } },
          { id: 'to-pmlink',   label: 'PM Link',     params: { adminTab: 'Table Ops', toView: 'pmlink' } },
          { id: 'to-shifts',   label: 'Shifts',      params: { adminTab: 'Table Ops', toView: 'shifts' }, need: ['canViewShift'] },
        ],
      },
      {
        id: 'admin-devices',
        label: 'Devices',
        params: { adminTab: 'Devices' },
        need: ['canViewIotDevices'],
        children: [
          { id: 'iot-dashboard',    label: 'Dashboard',       params: { adminTab: 'Devices', iotTab: 'Dashboard' } },
          { id: 'iot-registry',     label: 'Device Registry', params: { adminTab: 'Devices', iotTab: 'Device Registry' } },
          { id: 'iot-approvals',    label: 'Device Approvals',params: { adminTab: 'Devices', iotTab: 'Device Approvals' } },
          { id: 'iot-telemetry',    label: 'Telemetry',       params: { adminTab: 'Devices', iotTab: 'Telemetry' } },
          { id: 'iot-logs',         label: 'Device Logs',     params: { adminTab: 'Devices', iotTab: 'Device Logs' } },
          { id: 'iot-config',       label: 'Configuration',   params: { adminTab: 'Devices', iotTab: 'Configuration' } },
          { id: 'iot-quarantine',   label: 'Quarantine',      params: { adminTab: 'Devices', iotTab: 'Quarantine' } },
          { id: 'iot-details',      label: 'Details',         params: { adminTab: 'Devices', iotTab: 'Details' } },
          { id: 'iot-provisioning', label: 'Provisioning',    params: { adminTab: 'Devices', iotTab: 'Provisioning' } },
        ],
      },
      {
        id: 'admin-shared-links',
        label: 'Shared Links',
        params: { adminTab: 'Shared Links' },
        need: ['canViewIotDevices', 'canAdminTableOps'],
        children: [
          { id: 'sl-activity',  label: 'Activity Log', params: { adminTab: 'Shared Links', slTab: 'Activity Log' } },
          { id: 'sl-generator', label: 'Generator',    params: { adminTab: 'Shared Links', slTab: 'Generator' } },
        ],
      },
      {
        id: 'admin-archiver',
        label: 'Archiver',
        params: { adminTab: 'Archiver' },
        need: ['canManageOrchestrator'],
      },
      {
        id: 'admin-dbops',
        label: 'DB Ops',
        params: { adminTab: 'DB Ops' },
        need: ['canManageOrchestrator'],
      },
    ],
  },
  {
    id: 'agent-chat',
    label: 'Agent Chat',
    path: '/agent-chat',
  },
  {
    id: 'media',
    label: 'Media',
    path: '/media',
    need: ['canManageServices'],
    children: [
      { id: 'media-youtube',  label: 'YouTube Ingest',  params: { mediaTab: 'youtube' } },
      { id: 'media-library',  label: 'Audio Library',   params: { mediaTab: 'library' } },
      { id: 'media-projects', label: 'Music Projects',  params: { mediaTab: 'projects' } },
    ],
  },
  {
    id: 'telemetry-center',
    label: 'Telemetry Center',
    path: '/telemetry-center',
    need: ['canViewIotDevices'],
  },
  {
    id: 'metering',
    label: 'Metering',
    path: '/usage',
    need: ['canManageOrchestrator'],
    children: [
      { id: 'meter-usage',  label: 'Usage Dashboard', path: '/usage' },
      { id: 'meter-quotas', label: 'Quota Admin',     path: '/quota-admin' },
    ],
  },
  {
    id: 'legal',
    label: 'Legal',
    path: '/legal',
    need: ['legal.client.view', 'legal.case.view'],
    children: [
      { id: 'legal-cases',     label: 'Causas',       params: { tab: 'cases' } },
      { id: 'legal-clients',   label: 'Clientes',     params: { tab: 'clients' } },
      { id: 'legal-documents', label: 'Documentos',   params: { tab: 'documents' } },
      { id: 'legal-deadlines', label: 'Vencimientos', params: { tab: 'deadlines' } },
      { id: 'legal-templates', label: 'Plantillas',   params: { tab: 'templates' } },
    ],
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Filter the tree so only nodes the user is allowed to see remain.
 * A node is visible when:
 *   1. It has no `need` array (inherits parent visibility), OR
 *   2. The user holds `isSuperadmin` capability, OR
 *   3. ANY capability key in the node's `need` list is true.
 * Parent nodes are kept only if they still have at least one visible child.
 */
export function filterTreeByCaps(
  tree: NavNode[],
  caps: Record<string, boolean>,
): NavNode[] {
  const isSuperadmin = caps.isSuperadmin === true;
  const hasAny = (need: string[]) =>
    isSuperadmin || need.some(k => caps[k] === true);

  const walkNode = (node: NavNode): NavNode | null => {
    // If this node has a need gate and user doesn't qualify → prune
    if (node.need && node.need.length > 0 && !hasAny(node.need)) return null;

    // Recursively filter children
    if (node.children) {
      const kids = node.children.map(walkNode).filter(Boolean) as NavNode[];
      // If all children were pruned, and this node is a branch (no leaf path),
      // it's meaningless — prune it too.
      if (kids.length === 0 && !node.path && !node.params) return null;
      return { ...node, children: kids.length ? kids : undefined };
    }
    return node;
  };

  return tree.map(walkNode).filter(Boolean) as NavNode[];
}

/** Walk the tree and return the inherited `path` for a given nodeId. */
export function resolveNodePath(
  nodeId: string,
  tree: NavNode[] = NAV_TREE,
): string {
  const find = (nodes: NavNode[], inherited: string): string | null => {
    for (const n of nodes) {
      const p = n.path ?? inherited;
      if (n.id === nodeId) return p;
      if (n.children) {
        const found = find(n.children, p);
        if (found) return found;
      }
    }
    return null;
  };
  return find(tree, '/') ?? '/';
}

/**
 * Compute the deepest-matching breadcrumb trail for a given location.
 * Returns an array of labels from root → deepest matched ancestor/leaf.
 */
export function getBreadcrumb(
  pathname: string,
  searchParams: URLSearchParams,
  tree: NavNode[] = NAV_TREE,
): string[] {
  const dfs = (nodes: NavNode[], inheritedPath: string): string[] | null => {
    for (const n of nodes) {
      const nodePath = n.path ?? inheritedPath;
      if (pathname !== nodePath) continue;

      const paramsOk =
        !n.params ||
        Object.entries(n.params).every(([k, v]) => searchParams.get(k) === v);
      if (!paramsOk) continue;

      // Try children for a deeper match
      if (n.children) {
        const deeper = dfs(n.children, nodePath);
        if (deeper) return [n.label, ...deeper];
      }
      return [n.label];
    }
    return null;
  };
  return dfs(tree, '/') ?? [];
}
